/**
 * Keep club-option-fees lines tied to their parent club line.
 * - On parent remove (qty 0): also remove linked fee lines
 * - On load / cart update: remove orphaned fee lines (parent already gone)
 */
(function () {
  if (window.__BGCLinkedFeesInit) return;
  window.__BGCLinkedFeesInit = true;

  const FEE_HANDLE =
    (window.theme && window.theme.clubOptionFeeHandle) ||
    (window.BGCPageflyOptionConfig && window.BGCPageflyOptionConfig.feeProductHandle) ||
    'club-option-fees';

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function isFeeLine(item) {
    if (!item) return false;
    const handle = item.handle || item.product_handle || '';
    const props = item.properties || {};
    return handle === FEE_HANDLE || props._bgc_linked === 'true';
  }

  function isParentCandidate(item) {
    if (!item || isFeeLine(item)) return false;
    const props = item.properties || {};
    if (props._bgc_link_id) return true;
    // Legacy: parent lines store option labels without leading underscore
    return Object.keys(props).some(function (key) {
      return key === 'Extend Club' || key === 'Regrip';
    });
  }

  function findLinkedFeeLines(cart, parentItem) {
    if (!cart || !parentItem || !Array.isArray(cart.items)) return [];
    const props = parentItem.properties || {};
    const linkId = props._bgc_link_id || '';
    const title = parentItem.product_title || parentItem.title || '';

    return cart.items.filter(function (item) {
      if (!isFeeLine(item)) return false;
      const feeProps = item.properties || {};
      if (linkId && feeProps._bgc_link_id === linkId) return true;
      if (!linkId && feeProps._bgc_for && title && feeProps._bgc_for === title) return true;
      return false;
    });
  }

  function findOrphanFeeLines(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    const parents = cart.items.filter(function (item) {
      return !isFeeLine(item);
    });
    const linkIds = new Set();
    const titles = new Set();
    parents.forEach(function (item) {
      const props = item.properties || {};
      if (props._bgc_link_id) linkIds.add(props._bgc_link_id);
      const title = item.product_title || item.title || '';
      if (title) titles.add(title);
    });

    return cart.items.filter(function (item) {
      if (!isFeeLine(item)) return false;
      const props = item.properties || {};
      if (props._bgc_link_id) return !linkIds.has(props._bgc_link_id);
      if (props._bgc_for) return !titles.has(props._bgc_for);
      // Fee line with no link metadata — remove if no visible parents remain
      return parents.length === 0;
    });
  }

  async function fetchCart() {
    const response = await fetch(cartRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('cart.js failed');
    return response.json();
  }

  function getSectionsPayload(cartItemsEl) {
    if (!cartItemsEl || typeof cartItemsEl.getSectionsToRender !== 'function') {
      return {
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      };
    }
    return {
      sections: cartItemsEl.getSectionsToRender().map(function (section) {
        return section.section;
      }),
      sections_url: window.location.pathname,
    };
  }

  async function applyUpdates(updates, cartItemsEl) {
    const payload = Object.assign({ updates: updates }, getSectionsPayload(cartItemsEl));
    const response = await fetch(window.routes?.cart_update_url || cartRoot() + 'cart/update.js', {
      ...fetchConfig(),
      body: JSON.stringify(payload),
    });
    const text = await response.text();
    return JSON.parse(text);
  }

  function renderParsedState(cartItemsEl, parsedState, line, name, variantId) {
    if (!cartItemsEl || !parsedState || parsedState.errors) return;

    cartItemsEl.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

    if (typeof cartItemsEl.getSectionsToRender === 'function') {
      cartItemsEl.getSectionsToRender().forEach(function (section) {
        const host = document.getElementById(section.id);
        if (!host || !parsedState.sections || !parsedState.sections[section.section]) return;
        const elementToReplace = host.querySelector(section.selector) || host;
        elementToReplace.innerHTML = cartItemsEl.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      });
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: parsedState,
        variantId: variantId,
      });
    }

    setTimeout(function () {
      scheduleLockBundleLinePrices();
    }, 0);

    if (parsedState.item_count === 0 && cartDrawerWrapper && typeof trapFocus === 'function') {
      const empty = cartDrawerWrapper.querySelector('.drawer__inner-empty');
      const link = cartDrawerWrapper.querySelector('a');
      if (empty && link) trapFocus(empty, link);
    } else if (document.querySelector('.cart-item') && cartDrawerWrapper && typeof trapFocus === 'function') {
      trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
    }
  }

  let orphanCleanupPromise = null;

  async function cleanupOrphanFees(cartItemsEl) {
    if (orphanCleanupPromise) return orphanCleanupPromise;

    orphanCleanupPromise = (async function () {
      try {
        const cart = await fetchCart();
        const orphans = findOrphanFeeLines(cart);
        if (!orphans.length) return null;

        const updates = {};
        orphans.forEach(function (item) {
          updates[item.key] = 0;
        });

        const host =
          cartItemsEl ||
          document.querySelector('cart-drawer-items') ||
          document.querySelector('cart-items');
        const parsedState = await applyUpdates(updates, host);
        if (host) renderParsedState(host, parsedState, null, null, null);
        return parsedState;
      } catch (error) {
        console.warn('[BGC] orphan fee cleanup skipped', error);
        return null;
      } finally {
        orphanCleanupPromise = null;
      }
    })();

    return orphanCleanupPromise;
  }

  function patchCartItemsClass(Ctor) {
    if (!Ctor || !Ctor.prototype || Ctor.prototype.__bgcLinkedFeesPatched) return;
    Ctor.prototype.__bgcLinkedFeesPatched = true;

    const originalUpdateQuantity = Ctor.prototype.updateQuantity;

    Ctor.prototype.updateQuantity = function (line, quantity, name, variantId) {
      const self = this;
      const qty = Number(quantity);

      if (qty !== 0) {
        return originalUpdateQuantity.call(self, line, quantity, name, variantId);
      }

      fetchCart()
        .then(function (cart) {
          const item = cart.items[Number(line) - 1];
          if (!item || isFeeLine(item) || !isParentCandidate(item)) {
            return originalUpdateQuantity.call(self, line, quantity, name, variantId);
          }

          const linked = findLinkedFeeLines(cart, item);
          if (!linked.length) {
            return originalUpdateQuantity.call(self, line, quantity, name, variantId);
          }

          self.enableLoading(line);

          const updates = {};
          updates[item.key] = 0;
          linked.forEach(function (fee) {
            updates[fee.key] = 0;
          });

          return applyUpdates(updates, self)
            .then(function (parsedState) {
              if (parsedState.errors) {
                self.updateLiveRegions(line, parsedState.errors);
                return;
              }
              renderParsedState(self, parsedState, line, name, variantId);
              self.updateLiveRegions(line, '');
            })
            .finally(function () {
              self.disableLoading(line);
            });
        })
        .catch(function (error) {
          console.warn('[BGC] linked fee remove failed, falling back', error);
          originalUpdateQuantity.call(self, line, quantity, name, variantId);
        });
    };
  }

  function tryPatch() {
    patchCartItemsClass(customElements.get('cart-items'));
    patchCartItemsClass(customElements.get('cart-drawer-items'));
  }

  function formatMoneyCents(cents) {
    const value = Number(cents) || 0;
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      try {
        return Shopify.formatMoney(value, window.theme?.moneyFormat || undefined);
      } catch (e) {
        /* fall through */
      }
    }
    return '$' + (value / 100).toFixed(2);
  }

  function applyBundlePriceToRow(row) {
    if (!row) return;
    const finalCents = row.getAttribute('data-bgc-bundle-final');
    if (finalCents == null || finalCents === '') return;

    const originalCents = row.getAttribute('data-bgc-bundle-original') || finalCents;
    const finalLabel = formatMoneyCents(finalCents);
    const originalLabel = formatMoneyCents(originalCents);
    const wrapper = row.querySelector('.cart-item__price-wrapper');
    if (!wrapper) return;

    const current = wrapper.querySelector('[data-bgc-line-price].price.price--end, [data-bgc-bundle-price].price.price--end');
    if (current && (current.textContent || '').trim() === finalLabel && !wrapper.querySelector('[data-hulkapps-line-price]')) {
      return;
    }

    // Skip unit-price block when rewriting
    const unitPrice = wrapper.querySelector('.unit-price');
    const unitHtml = unitPrice ? unitPrice.outerHTML : '';

    let priceHtml = '';
    if (String(originalCents) !== String(finalCents)) {
      priceHtml =
        '<div class="cart-item__discounted-prices" data-bgc-bundle-price>' +
        '<span class="visually-hidden">Regular price</span>' +
        '<s data-bgc-line-price class="cart-item__old-price price price--end">' +
        originalLabel +
        '</s>' +
        '<span class="visually-hidden">Sale price</span>' +
        '<span data-bgc-line-price class="price price--end">' +
        finalLabel +
        '</span></div>';
    } else {
      priceHtml =
        '<span data-bgc-line-price data-bgc-bundle-price class="price price--end">' + finalLabel + '</span>';
    }

    wrapper.innerHTML = priceHtml + unitHtml;
  }

  let lockBundlePricesTimer = null;
  function scheduleLockBundleLinePrices(root) {
    clearTimeout(lockBundlePricesTimer);
    lockBundlePricesTimer = setTimeout(function () {
      lockBundleLinePrices(root);
    }, 60);
  }

  function lockBundleLinePrices(root) {
    const scope = root || document;
    if (scope.__bgcApplyingBundlePrices) return;
    scope.__bgcApplyingBundlePrices = true;
    try {
      scope.querySelectorAll('.cart-item[data-bgc-bundle-final]').forEach(applyBundlePriceToRow);
    } finally {
      scope.__bgcApplyingBundlePrices = false;
    }
  }

  function boot() {
    tryPatch();
    // Elements may define slightly later
    setTimeout(tryPatch, 0);
    setTimeout(tryPatch, 250);
    cleanupOrphanFees();
    scheduleLockBundleLinePrices();
    setTimeout(function () {
      scheduleLockBundleLinePrices();
    }, 500);
    setTimeout(function () {
      scheduleLockBundleLinePrices();
    }, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('cart:updated', function () {
    cleanupOrphanFees();
    scheduleLockBundleLinePrices();
  });

  document.addEventListener('cart:refresh', function () {
    scheduleLockBundleLinePrices();
  });

  // After Dawn / Hulk rewrite cart HTML
  const cartHost = document.getElementById('CartDrawer') || document.getElementById('main-cart-items');
  if (cartHost && !window.__BGCPriceDomObserver) {
    window.__BGCPriceDomObserver = new MutationObserver(function () {
      scheduleLockBundleLinePrices(cartHost);
    });
    window.__BGCPriceDomObserver.observe(cartHost, { childList: true, subtree: true });
  }

  window.BGCLinkedFees = {
    isFeeLine: isFeeLine,
    findLinkedFeeLines: findLinkedFeeLines,
    cleanupOrphanFees: cleanupOrphanFees,
    lockBundleLinePrices: lockBundleLinePrices,
    visibleItemCount: function (cart) {
      if (!cart || !Array.isArray(cart.items)) return 0;
      var total = 0;
      cart.items.forEach(function (item) {
        if (isFeeLine(item)) return;
        total += item.quantity || 0;
      });
      return total;
    },
    createLinkId: function () {
      return 'bgc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 9);
    },
  };
})();
